export const DEFAULT_LANGUAGE = "en";
export const AUTOMATIC_LANGUAGE = "auto";

// Keep this list aligned with the approved Hoppy product requirement. The
// names intentionally remain in English in the Settings selector.
export const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "zh-CN", name: "Chinese (Simplified)" },
  { code: "zh-TW", name: "Chinese (Traditional)" },
  { code: "tr", name: "Turkish" },
  { code: "pt-BR", name: "Portuguese (Brazil)" },
  { code: "pt-PT", name: "Portuguese (Portugal)" },
  { code: "ja", name: "Japanese" },
  { code: "it", name: "Italian" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "nl", name: "Dutch" },
  { code: "cs", name: "Czech" },
  { code: "da", name: "Danish" },
  { code: "es", name: "Spanish" },
  { code: "no", name: "Norwegian" },
  { code: "nn", name: "Norwegian Nynorsk" },
  { code: "nb", name: "Norwegian (Bokmål)" },
  { code: "pl", name: "Polish" },
  { code: "so", name: "Somali" },
  { code: "fi", name: "Finnish" },
  { code: "sv", name: "Swedish" },
  { code: "th", name: "Thai" },
  { code: "ko", name: "Korean" },
];

export const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LANGUAGES.map(
  ({ code }) => code,
);

export function isSupportedLanguage(locale) {
  return SUPPORTED_LANGUAGE_CODES.includes(locale);
}

/**
 * Normalize Shopify/BCP-47 locale variants for automatic detection only.
 * Persisted manual values must pass isSupportedLanguage without normalization.
 */
export function matchSupportedLanguage(locale) {
  if (!locale || typeof locale !== "string") return DEFAULT_LANGUAGE;

  const cleaned = locale.trim().replaceAll("_", "-");
  if (!cleaned) return DEFAULT_LANGUAGE;

  const exact = SUPPORTED_LANGUAGE_CODES.find(
    (code) => code.toLowerCase() === cleaned.toLowerCase(),
  );
  if (exact) return exact;

  const lower = cleaned.toLowerCase();
  if (
    lower === "zh" ||
    lower === "zh-cn" ||
    lower === "zh-sg" ||
    lower.startsWith("zh-hans")
  ) {
    return "zh-CN";
  }
  if (
    lower === "zh-tw" ||
    lower === "zh-hk" ||
    lower === "zh-mo" ||
    lower.startsWith("zh-hant")
  ) {
    return "zh-TW";
  }
  if (lower === "pt" || lower.startsWith("pt-")) {
    return lower === "pt-br" ? "pt-BR" : "pt-PT";
  }

  const base = lower.split("-")[0];
  const baseMatch = SUPPORTED_LANGUAGE_CODES.find(
    (code) => code.toLowerCase() === base,
  );

  return baseMatch || DEFAULT_LANGUAGE;
}

export function resolveLanguagePreference({
  savedLanguage,
  staffLanguage,
  adminLocale,
} = {}) {
  // Only a missing personal preference inherits the legacy shop setting.
  // An explicit null means Automatic, even when the shop has a manual choice.
  const preferredLanguage =
    staffLanguage === undefined ? savedLanguage : staffLanguage;
  const manualLanguage = isSupportedLanguage(preferredLanguage)
    ? preferredLanguage
    : null;
  const automaticLanguage = matchSupportedLanguage(adminLocale);

  return {
    locale: manualLanguage || automaticLanguage,
    manualLanguage,
    automaticLanguage,
  };
}

/** Shopify user IDs become MongoDB map keys, so accept decimal IDs only. */
export function normalizeStaffId(value) {
  if (typeof value === "number" && !Number.isSafeInteger(value)) return null;
  if (typeof value !== "string" && typeof value !== "number") return null;

  const id = String(value);
  return /^[1-9]\d*$/.test(id) ? id : null;
}
